// Types shared by the API and the web client. The server validates incoming
// payloads with zod schemas that mirror these shapes.

export type Locale = 'es' | 'en';
export const LOCALES: readonly Locale[] = ['es', 'en'];
export const DEFAULT_LOCALE: Locale = 'es';

export type PaymentMethod = 'cash' | 'check' | 'transfer' | 'credit';
export const PAYMENT_METHODS: readonly PaymentMethod[] = ['cash', 'check', 'transfer', 'credit'];

export type InvoiceStatus = 'paid' | 'pending';
export const INVOICE_STATUSES: readonly InvoiceStatus[] = ['paid', 'pending'];

/**
 * A quotation's own lifecycle. There is deliberately no 'expired' state: expiry
 * is derived from `validUntil` at read time, so it needs no scheduled job and
 * cannot go stale.
 */
export type QuotationStatus = 'draft' | 'sent' | 'accepted' | 'rejected';
export const QUOTATION_STATUSES: readonly QuotationStatus[] =
  ['draft', 'sent', 'accepted', 'rejected'];

export interface Currency {
  id: number;
  code: string;
  name: string;
  symbol: string;
  decimals: number;
  thousandSeparator: string;
  decimalSeparator: string;
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  locale: Locale;
  isActive: boolean;
  createdAt: string;
}

export interface Client {
  id: number;
  name: string;
  taxId: string;
  phone: string;
  email: string;
  address: string;
  isActive: boolean;
  createdAt: string;
  /** Present on list responses: how many invoices reference this client. */
  invoiceCount?: number;
}

export interface Product {
  id: number;
  code: string;
  name: string;
  unitPrice: number;
  isActive: boolean;
  createdAt: string;
}

export interface InvoiceLine {
  id: number;
  productId: number | null;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
  position: number;
}

/** A line as submitted by the client; totals are recomputed server-side. */
export interface InvoiceLineInput {
  productId: number | null;
  code: string;
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface InvoiceSummary {
  id: number;
  number: number;
  issuedAt: string;
  clientId: number;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  sellerId: number;
  sellerName: string;
  paymentMethod: PaymentMethod;
  status: InvoiceStatus;
  currencySymbol: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  lineCount: number;
  /** When this document was last emailed to the client, if ever. */
  lastEmailedAt: string | null;
  /** Set when the invoice was created from a quotation. */
  quotationId: number | null;
  quotationNumber: number | null;
  /**
   * Where this invoice stands with the AEAT register. Null when VERI*FACTU has
   * never been switched on, so a deployment that is not under the regime shows
   * nothing about it anywhere.
   */
  verifactu?: VerifactuState | null;
}

export interface Invoice extends InvoiceSummary {
  /** Only on the detail response — the PDF letterhead needs them. */
  clientTaxId: string;
  clientAddress: string;
  taxRate: number;
  currencyCode: string;
  notes: string;
  lines: InvoiceLine[];
}

export interface InvoiceInput {
  clientId: number;
  sellerId: number;
  issuedAt: string;
  paymentMethod: PaymentMethod;
  status: InvoiceStatus;
  notes: string;
  lines: InvoiceLineInput[];
}

// ── quotations ──────────────────────────────────────────────────────────────
// A quotation mirrors an invoice's shape but is a separate document with its
// own numbering. It is never counted as revenue, which is why it lives in its
// own tables rather than behind a type flag on `invoices`.

export interface QuotationSummary {
  id: number;
  number: number;
  issuedAt: string;
  validUntil: string;
  clientId: number;
  clientName: string;
  clientPhone: string;
  clientEmail: string;
  sellerId: number;
  sellerName: string;
  status: QuotationStatus;
  /** Derived: past validUntil and not yet accepted or rejected. */
  isExpired: boolean;
  currencySymbol: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  lineCount: number;
  /** Set once the quotation has been turned into an invoice. */
  convertedInvoiceId: number | null;
  convertedInvoiceNumber: number | null;
  lastEmailedAt: string | null;
}

export interface Quotation extends QuotationSummary {
  clientTaxId: string;
  clientAddress: string;
  taxRate: number;
  currencyCode: string;
  notes: string;
  lines: InvoiceLine[];
}

export interface QuotationInput {
  clientId: number;
  sellerId: number;
  issuedAt: string;
  validUntil: string;
  status: QuotationStatus;
  notes: string;
  lines: InvoiceLineInput[];
}

export interface QuotationTotals {
  count: number;
  draftCount: number;
  sentCount: number;
  acceptedCount: number;
  rejectedCount: number;
  subtotal: number;
  taxTotal: number;
  total: number;
  /** Value of quotations still live — sent, in date, not yet decided. */
  openValue: number;
}

/** How many days a new quotation is valid for by default. */
export const DEFAULT_QUOTATION_VALIDITY_DAYS = 30;

// ── suppliers and purchase invoices ─────────────────────────────────────────

export type SupplierInvoiceStatus = 'pending' | 'paid';
export const SUPPLIER_INVOICE_STATUSES: readonly SupplierInvoiceStatus[] = ['pending', 'paid'];

export interface Supplier {
  id: number;
  name: string;
  taxId: string;
  phone: string;
  email: string;
  address: string;
  notes: string;
  isActive: boolean;
  createdAt: string;
  /** Present on list responses. */
  invoiceCount?: number;
  totalBilled?: number;
}

/** The uploaded original — a scan or the supplier's own PDF. */
export interface SupplierInvoiceFile {
  name: string;
  size: number;
  type: string;
  /** Path to download it from, session-gated. */
  url: string;
}

export interface SupplierInvoice {
  id: number;
  supplierId: number;
  supplierName: string;
  supplierTaxId: string;
  /** The supplier's own reference, not ours. */
  number: string;
  issuedOn: string;
  receivedOn: string | null;
  dueOn: string | null;
  description: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  currencyCode: string;
  currencySymbol: string;
  status: SupplierInvoiceStatus;
  paidOn: string | null;
  notes: string;
  file: SupplierInvoiceFile | null;
  lastExportedAt: string | null;
  createdAt: string;
}

export interface SupplierInvoiceInput {
  supplierId: number;
  number: string;
  issuedOn: string;
  receivedOn: string | null;
  dueOn: string | null;
  description: string;
  subtotal: number;
  taxTotal: number;
  status: SupplierInvoiceStatus;
  paidOn: string | null;
  notes: string;
}

export interface SupplierInvoiceTotals {
  count: number;
  pendingCount: number;
  paidCount: number;
  subtotal: number;
  taxTotal: number;
  total: number;
  /** Value of purchase invoices not yet marked paid. */
  outstanding: number;
}

// ── accounting export ───────────────────────────────────────────────────────

export type ExportDocumentKind = 'sales' | 'purchases';

/** One row in the export picker. */
export interface ExportCandidate {
  id: number;
  kind: ExportDocumentKind;
  number: string;
  issuedOn: string;
  partyName: string;
  total: number;
  currencySymbol: string;
  /** Purchases only: false when no original file was uploaded. */
  hasFile: boolean;
  lastExportedAt: string | null;
}

export interface ExportSelection {
  salesInvoiceIds: number[];
  supplierInvoiceIds: number[];
}

export interface ExportPreview {
  sales: ExportCandidate[];
  purchases: ExportCandidate[];
  salesTotal: number;
  purchasesTotal: number;
  currencySymbol: string;
}

export interface SendExportInput extends ExportSelection {
  to: string;
  cc?: string;
  subject: string;
  message: string;
}

// ── reports ─────────────────────────────────────────────────────────────────

/** One period's figures for a client — a year, or the whole history. */
export interface ClientPeriodSummary {
  /** Calendar year, or null for the all-time row. */
  year: number | null;
  invoiceCount: number;
  subtotal: number;
  taxTotal: number;
  total: number;
  paidTotal: number;
  outstanding: number;
  firstInvoiceOn: string | null;
  lastInvoiceOn: string | null;
}

export interface ClientReportRow extends ClientPeriodSummary {
  clientId: number;
  clientName: string;
  clientTaxId: string;
}

export interface ClientReport {
  /** One row per client for the chosen period, biggest first. */
  rows: ClientReportRow[];
  totals: ClientPeriodSummary;
  /** Years that have any invoices, for the period picker. */
  availableYears: number[];
  currencySymbol: string;
}

/** A single client's history, year by year. */
export interface ClientDetailReport {
  clientId: number;
  clientName: string;
  clientTaxId: string;
  years: ClientPeriodSummary[];
  totals: ClientPeriodSummary;
  currencySymbol: string;
}

// ── mail server ─────────────────────────────────────────────────────────────

/** How the relay is reached: plain, STARTTLS on connect, or implicit TLS. */
export type MailSecurity = 'none' | 'starttls' | 'tls';

/**
 * The SMTP relay invoices, quotations and the accountant export are sent
 * through. Held in the database rather than the environment so it can be
 * changed from Settings without a redeploy or a restart.
 */
export interface MailSettings {
  /** Off means the app reports that email is not configured, rather than failing. */
  enabled: boolean;
  host: string;
  port: number;
  security: MailSecurity;
  username: string;
  /**
   * Verify the certificate against this name instead of the host dialled — for
   * a relay on this machine reached at 127.0.0.1 but holding a certificate for
   * its public hostname. Empty means verify against the host.
   */
  tlsServername: string;
  /** The envelope sender: the mailbox the relay has authorised. */
  fromName: string;
  fromEmail: string;
  /** Comma-separated; every outgoing message is copied to these. */
  bcc: string;
  /** Never returned: the API reports only whether a password is stored. */
  hasPassword: boolean;
  /** True while the row still holds what the SMTP_* variables were seeded from. */
  fromEnvironment: boolean;
}

/** The password is write-only; omitting it leaves the stored one untouched. */
export interface MailSettingsInput extends Omit<MailSettings, 'hasPassword' | 'fromEnvironment'> {
  password?: string;
}

// ── VERI*FACTU ──────────────────────────────────────────────────────────────

/** Which AEAT installation records go to. Sandbox registers nothing. */
export type VerifactuEnvironment = 'sandbox' | 'production';

/** A personal or company certificate, or an organisation seal certificate. */
export type VerifactuCertificateType = 'certificate' | 'seal';

/**
 * What to tell AEAT about an invoice issued at 0% — exempt under one of the
 * articles (E…) or outside the scope of VAT (N…). A fact about the business, so
 * it is configured once rather than asked per invoice.
 */
export const VERIFACTU_ZERO_RATE_CODES =
  ['E1', 'E2', 'E3', 'E4', 'E5', 'E6', 'N1', 'N2'] as const;
export type VerifactuZeroRateCode = (typeof VERIFACTU_ZERO_RATE_CODES)[number];

/** The VAT regimes this application can declare. 01 — general — covers most. */
export const VERIFACTU_REGIME_KEYS =
  ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '14', '15', '17', '18', '19', '20'] as const;
export type VerifactuRegimeKey = (typeof VERIFACTU_REGIME_KEYS)[number];

/**
 * How the AEAT register is configured. The certificate and its password are
 * write-only: the API reports what could be read *about* the certificate, never
 * the certificate itself.
 */
export interface VerifactuSettings {
  enabled: boolean;
  environment: VerifactuEnvironment;
  certificateType: VerifactuCertificateType;

  /** The NIF records are filed under. Blank falls back to the company tax ID. */
  issuerNif: string;
  /** The name AEAT shows against that NIF. Blank falls back to the company name. */
  issuerName: string;
  /**
   * Prefixes the invoice number to make the series AEAT sees. The series and
   * number together must be unique for the NIF for all time, which a bare
   * sequence shared with a previous system would not be.
   */
  seriesPrefix: string;

  zeroRateCode: VerifactuZeroRateCode;
  regimeKey: VerifactuRegimeKey;

  /** Identifies this software in every record. */
  systemName: string;
  systemId: string;
  systemVersion: string;
  installationId: string;
  /** Who produces the software. Blank falls back to the issuer. */
  producerName: string;
  producerNif: string;

  /** Register an invoice as it is saved, rather than waiting to be told. */
  autoSubmit: boolean;

  // ── read-only, about the stored certificate ──
  hasCertificate: boolean;
  certificateFilename: string;
  certificateSubject: string;
  certificateIssuer: string;
  /** Null when no certificate is stored. */
  certificateNotAfter: string | null;
  /** Negative once expired; null when unknown. */
  certificateDaysLeft: number | null;

  /**
   * What the issuer and producer actually resolve to once the blanks are filled
   * in from the company settings — so the form can show what will be sent.
   */
  effectiveIssuerNif: string;
  effectiveIssuerName: string;
  /** Reasons this configuration cannot be used yet, as i18n keys. */
  problems: string[];
}

/** Both secrets are write-only; omitting either leaves the stored one alone. */
export interface VerifactuSettingsInput
  extends Omit<VerifactuSettings,
  'hasCertificate' | 'certificateFilename' | 'certificateSubject' | 'certificateIssuer'
  | 'certificateNotAfter' | 'certificateDaysLeft'
  | 'effectiveIssuerNif' | 'effectiveIssuerName' | 'problems'> {
  certificatePassword?: string;
}

/**
 * A record's progress with AEAT.
 *   pending   generated and chained, not yet accepted — waiting or retrying
 *   accepted  registered
 *   warned    registered, with reservations AEAT reported alongside
 *   rejected  refused on its content; needs a corrected record
 *   failed    undeliverable after enough attempts to stop trying
 */
export type VerifactuRecordStatus = 'pending' | 'accepted' | 'warned' | 'rejected' | 'failed';

/** A registration or a withdrawal. */
export type VerifactuRecordType = 'alta' | 'anulacion';

/** One link in the chain, as the UI sees it. Never editable. */
export interface VerifactuRecord {
  id: number;
  invoiceId: number | null;
  /** The invoice number, for a record whose invoice has since been deleted. */
  invoiceNumber: number | null;
  recordType: VerifactuRecordType;
  seriesNumber: string;
  issueDate: string;
  /** When the record was generated, with the timezone offset AEAT requires. */
  recordAt: string;
  hash: string;
  previousHash: string | null;
  status: VerifactuRecordStatus;
  /** AEAT's own wording for the verdict. */
  aeatState: string;
  /** The receipt for an accepted record. */
  csv: string;
  errorCode: string;
  errorMessage: string;
  attempts: number;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  createdAt: string;
  /** True when this record amends one AEAT rejected. */
  subsanacion: boolean;
}

/** Where one invoice stands, summarised for a list row. */
export interface VerifactuState {
  /** The registration's state, or null when the invoice has no record. */
  status: VerifactuRecordStatus | null;
  /** AEAT's receipt, once registered. */
  csv: string;
  /** True once a withdrawal has been accepted. */
  cancelled: boolean;
  errorCode: string;
  errorMessage: string;
}

/** The queue's health, shown at the top of the VERI*FACTU page. */
export interface VerifactuQueue {
  pending: number;
  rejected: number;
  failed: number;
  accepted: number;
  /** The newest record's fingerprint — the head of the chain. */
  chainHead: string | null;
  chainLength: number;
}

// ── backups ─────────────────────────────────────────────────────────────────

export type BackupFrequency = 'daily' | 'weekly';
export type BackupAuth = 'password' | 'key';
export type BackupStatus = 'running' | 'success' | 'failed';

export interface BackupSettings {
  /** Master switch for the schedule. Each destination has its own toggle. */
  enabled: boolean;
  frequency: BackupFrequency;
  hour: number;
  minute: number;
  /** 0 = Sunday. Only used when frequency is 'weekly'. */
  dayOfWeek: number;
  /** Include the uploaded files under storage/, not just the database. */
  includeFiles: boolean;

  /** A directory on this server — fast to restore from. */
  localEnabled: boolean;
  localPath: string;
  /** Archives kept here; older ones are pruned after each run. */
  localKeepCount: number;

  /** Another machine over SFTP — survives losing this one. */
  sftpEnabled: boolean;
  sftpKeepCount: number;
  sftpHost: string;
  sftpPort: number;
  sftpUsername: string;
  sftpPath: string;
  sftpAuth: BackupAuth;
  /** Never returned: the API reports only whether a secret is stored. */
  hasSftpPassword: boolean;
  hasSftpPrivateKey: boolean;
  lastRunAt: string | null;
  /** Computed: when the next scheduled run is due. */
  nextRunAt: string | null;
}

/** Secrets are write-only; omitting one leaves the stored value untouched. */
export interface BackupSettingsInput extends Omit<BackupSettings,
  'hasSftpPassword' | 'hasSftpPrivateKey' | 'lastRunAt' | 'nextRunAt'> {
  sftpPassword?: string;
  sftpPrivateKey?: string;
  sftpPassphrase?: string;
}

export interface BackupRun {
  id: number;
  startedAt: string;
  finishedAt: string | null;
  status: BackupStatus;
  triggerType: 'scheduled' | 'manual';
  /** Which destinations this run wrote to, e.g. 'local' or 'local+sftp'. */
  destination: string;
  filename: string;
  sizeBytes: number | null;
  tableCount: number | null;
  rowCount: number | null;
  fileCount: number | null;
  error: string;
}

export interface Settings {
  companyName: string;
  taxId: string;
  address: string;
  city: string;
  region: string;
  postalCode: string;
  phone: string;
  email: string;
  taxRate: number;
  currencyCode: string;
  logoPath: string;
  /** Printed at the foot of every invoice and quotation PDF. */
  bankName: string;
  bankAccount: string;
  /** Where the accounting export is addressed by default. */
  accountantName: string;
  accountantEmail: string;
  defaultLocale: Locale;
}

/** Totals across the current invoice filter, shown above the invoice list. */
export interface InvoiceTotals {
  count: number;
  paidCount: number;
  pendingCount: number;
  subtotal: number;
  taxTotal: number;
  total: number;
  outstanding: number;
}

export interface Page<T> {
  items: T[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface ApiError {
  error: string;
  /** i18n key the client can translate; `error` is the English fallback. */
  code?: string;
  fields?: Record<string, string>;
}
